import CancellableHelper from "@/requests/CancellableHelper";
import GenericTableRequest from "@/models/api/GenericTableRequest";

import GenericTableResponse from "@/models/api/GenericTableResponse";
import { OfficeProfile } from "@/models/api/administration/offices/OfficeProfile";
import { Printer } from "@/models/api/administration/printers/Printer";
import { PrinterCommand } from "@/models/api/administration/printers/PrinterCommand";
import { PrinterCommandListResponse } from "@/models/api/administration/printers/PrinterCommand";
import { SetFiscalPrinterDbSettingsModel } from "@/models/api/administration/printers/SetFiscalPrinterDbSettingsModel";
import { EditPrinter } from "@/models/api/administration/printers/EditPrinter";
import { AddPrinter } from "@/models/api/administration/printers/AddPrinter";
import { DefaultPrinter } from "@/models/api/administration/printers/DefaultPrinter";

//#region Routes
export const GET_PRINTER_OFFICE_PROFILES = "/printer/OfficeProfiles";
export const ADD_PRINTER = "/printer/Add";
export const EDIT_PRINTER = "/printer/Edit";
export const SET_DEFAULT_PRINTER = "/printer/SetDefault";
export const DELETE_PRINTER = "/printer/${PrinterId}/Delete";
export const GET_PRINTER = "/printer/${PrinterId}";
export const GET_PRINTER_COMMANDS = "/printer/${PrinterId}/Commands";
export const RESEND_COMMAND = "/printer/${CommandId}/Resend";
export const GET_OFFICE_PROFILE_PRINTERS = "/printer/${OfficeProfileId}/Printers";
export const CHECK_PRINTER_STATUS = "/printer/${PrinterId}/CheckStatus";
export const PRINT_DAILY_REPORT = "/printer/${PrinterId}/PrintDailyReport";
export const GET_PRINTER_SETTINGS = "/printer/${PrinterId}/Settings";
export const RESTART_PRINTER_SERVICE = "/printer/${PrinterId}/Restart/${ServiceName}";
export const SET_FISAL_PRINTER_DB_SETTINGS = "/printer/${PrinterId}/DbSetting";
//#endregion

const _getPrinterOfficeProfilesTable = new CancellableHelper<Array<OfficeProfile>>(
  GET_PRINTER_OFFICE_PROFILES,
  OfficeProfile
);
_getPrinterOfficeProfilesTable.jsonSerializer = true;
export const getPrinterOfficeProfilesTable = async (): Promise<Array<OfficeProfile>> => {
  return await _getPrinterOfficeProfilesTable.get();
};

const _getPrintersList = new CancellableHelper<Array<Printer>>(GET_OFFICE_PROFILE_PRINTERS, Printer);
_getPrintersList.jsonSerializer = true;
export const getPrintersList = async (officeProfileId: number): Promise<Array<Printer>> => {
  return await _getPrintersList.get({ OfficeProfileId: officeProfileId });
};

const _getPrinterCommandsList = new CancellableHelper<GenericTableResponse<PrinterCommand>>(
  GET_PRINTER_COMMANDS,
  PrinterCommandListResponse
);
_getPrinterCommandsList.jsonSerializer = true;
export const getPrinterCommandsList = async (
  printerId: number,
  genericTableRequest: GenericTableRequest
): Promise<GenericTableResponse<PrinterCommand>> => {
  return await _getPrinterCommandsList.get({
    PrinterId: printerId,
    ...genericTableRequest,
  });
};

const _checkPrinterStatus = new CancellableHelper<null>(CHECK_PRINTER_STATUS, null, true);
export const checkPrinterStatus = async (printerId: number): Promise<null> => {
  return await _checkPrinterStatus.post({ PrinterId: printerId });
};

const _printDailyReport = new CancellableHelper<null>(PRINT_DAILY_REPORT, null, true);
export const printDailyReport = async (printerId: number): Promise<null> => {
  return await _printDailyReport.post({ PrinterId: printerId });
};

const _getPrinterSettings = new CancellableHelper<null>(GET_PRINTER_SETTINGS, null, true);
export const getPrinterSettings = async (printerId: number): Promise<null> => {
  return await _getPrinterSettings.post({ PrinterId: printerId });
};

const _restartPrinterService = new CancellableHelper<null>(RESTART_PRINTER_SERVICE, null, true);
export const restartPrinterService = async (printerId: number, serviceName: string): Promise<null> => {
  return await _restartPrinterService.post({
    PrinterId: printerId,
    ServiceName: serviceName,
  });
};

const _setFisalPrinterDbSettings = new CancellableHelper<null>(SET_FISAL_PRINTER_DB_SETTINGS, null, true);
export const setFisalPrinterDbSettings = async (
  printerId: number,
  setting: SetFiscalPrinterDbSettingsModel
): Promise<null> => {
  return await _setFisalPrinterDbSettings.post({ PrinterId: printerId }, setting);
};

const _postResendCommand = new CancellableHelper<null>(RESEND_COMMAND, null, true);
export const postResendCommand = async (commandId: string): Promise<null> => {
  return await _postResendCommand.post({ CommandId: commandId });
};

const _editPrinter = new CancellableHelper<null>(EDIT_PRINTER, null, true);
export const editPrinter = async (printer: EditPrinter): Promise<null> => {
  return await _editPrinter.post(null, printer);
};

const _addPrinter = new CancellableHelper<null>(ADD_PRINTER, null, true);
export const addPrinter = async (printer: AddPrinter): Promise<null> => {
  return await _addPrinter.post(null, printer);
};

const _setDefaultPrinter = new CancellableHelper<null>(SET_DEFAULT_PRINTER, null, true);
export const setDefaultPrinter = async (printer: DefaultPrinter): Promise<null> => {
  return await _setDefaultPrinter.post(null, printer);
};

const _deletePrinter = new CancellableHelper<null>(DELETE_PRINTER, null, true);
export const deletePrinter = async (printerId: number): Promise<null> => {
  return await _deletePrinter.post({ PrinterId: printerId });
};
